import { useMemo } from 'react';
import { createAllModelsStatsMatrix } from '../helpers/ProjectPreviewHelper';
import { PieChartData } from '../../../../shared/models/common/PieChartData';
import { ProjectStatsResponse } from '../../../../shared/services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { AccountResponse } from '../../../../shared/models/account/AccountResponse';
import { ChatType, projectChatType } from '../../../../shared/types/monitor/ChatType';
import { expandSelectedChatTypes, getMonitorTypesForChatType } from '../../../../shared/helpers/MonitorTypeHelpers';

export const useAllModelsStatsMatrix = (
  isProjectDailyStatsLoading: boolean,
  isMonitorPromptsLoading: boolean,
  isEventsLoading: boolean,
  projectStatsResponse: ProjectStatsResponse,
  firmName: string,
  accountResponse: AccountResponse | null,
  shareOfVoiceData: PieChartData[],
  keywordsMap: Map<string, string>,
  hasExplicitModelFilter?: boolean,
  selectedChatTypes?: ChatType[]
) => {
  const allowedChatTypes = useMemo(() => {
    if (!hasExplicitModelFilter) return null;
    if (!selectedChatTypes || selectedChatTypes.length === 0) return new Set<string>();
    return expandSelectedChatTypes(selectedChatTypes);
  }, [hasExplicitModelFilter, selectedChatTypes]);

  return useMemo(() => {
    if (
      isProjectDailyStatsLoading ||
      isMonitorPromptsLoading ||
      isEventsLoading ||
      !projectStatsResponse ||
      !accountResponse ||
      !keywordsMap ||
      keywordsMap.size === 0
    ) {
      return {
        matrix: null,
        selectedKeywords: [],
      };
    }

    const data = projectStatsResponse.monitorPromptsKeywordsStatsResponse;
    const filteredData = allowedChatTypes
      ? data.filter((entry) => entry.chatType && allowedChatTypes.has(entry.chatType))
      : data;

    const visibleChatTypes = allowedChatTypes
      ? projectChatType(accountResponse, true).filter((ct) => allowedChatTypes.has(ct))
      : undefined;

    return createAllModelsStatsMatrix(
      filteredData,
      firmName,
      accountResponse,
      shareOfVoiceData,
      keywordsMap,
      visibleChatTypes
    );
  }, [
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isEventsLoading,
    projectStatsResponse,
    firmName,
    accountResponse,
    shareOfVoiceData,
    keywordsMap,
    allowedChatTypes,
  ]);
};
